import {
    Box,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { formatVolume } from '../utils/format';
import { CONTROL_HINT_SX } from './tradeControlStyles';
import type { TradeSortField } from '../hooks/useTradeFiltering';

const MIN_VOLUME_OPTIONS = [0, 1_000, 10_000, 100_000];

const LABEL_SX = {
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: 'text.secondary',
};

const GROUP_SX = {
    '& .MuiToggleButton-root': {
        px: 1,
        py: 0.25,
        fontSize: '10px',
        fontWeight: 600,
        textTransform: 'none' as const,
    },
};

interface TradeControlsPanelProps {
    sortField: TradeSortField;
    sortAsc: boolean;
    minVolume: number;
    /**
     * False when no window bounds the list. Value sorts and thresholds are
     * disabled then: over a cursor-paginated newest-first list they would claim
     * something about rows nobody has fetched.
     */
    canRankByValue: boolean;
    /** Rows shown vs. rows in the window, for the footer count. */
    shownCount: number;
    windowCount: number;
    /** True while pages are still unfetched, so the window count is a floor. */
    hasMore: boolean;
    resetDisabled: boolean;
    onSortFieldChange: (field: TradeSortField) => void;
    onToggleSortDirection: () => void;
    onMinVolumeChange: (min: number) => void;
    onReset: () => void;
}

/** Sort and volume-threshold controls for the trades list. Presentational. */
export function TradeControlsPanel({
    sortField,
    sortAsc,
    minVolume,
    canRankByValue,
    shownCount,
    windowCount,
    hasMore,
    resetDisabled,
    onSortFieldChange,
    onToggleSortDirection,
    onMinVolumeChange,
    onReset,
}: TradeControlsPanelProps) {
    const theme = useTheme();

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                px: 1,
                py: 1.25,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.action.hover, 0.03),
                flexShrink: 0,
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.625 }}>
                <Typography sx={LABEL_SX}>Sort by</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        aria-label="Sort field"
                        value={sortField}
                        onChange={(_, v) => v && onSortFieldChange(v)}
                        sx={{ flexGrow: 1, ...GROUP_SX }}
                    >
                        <ToggleButton value="time" sx={{ flexGrow: 1 }}>
                            Time
                        </ToggleButton>
                        <ToggleButton
                            value="volume"
                            disabled={!canRankByValue}
                            sx={{ flexGrow: 1 }}
                        >
                            Volume
                        </ToggleButton>
                        <ToggleButton
                            value="price"
                            disabled={!canRankByValue}
                            sx={{ flexGrow: 1 }}
                        >
                            Price
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Tooltip
                        title={sortAsc ? 'Sort descending' : 'Sort ascending'}
                        placement="left"
                    >
                        {/* span wrapper: a disabled button fires no events, so
                            Tooltip needs a live element — and the disabled case
                            is exactly when the tooltip has something to say */}
                        <Box component="span" sx={{ display: 'flex' }}>
                            <Box
                                component="button"
                                type="button"
                                aria-label={
                                    sortAsc
                                        ? 'Sort descending'
                                        : 'Sort ascending'
                                }
                                disabled={!canRankByValue}
                                onClick={onToggleSortDirection}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 28,
                                    height: 26,
                                    p: 0,
                                    flexShrink: 0,
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: '4px',
                                    bgcolor: 'transparent',
                                    color: canRankByValue
                                        ? 'text.primary'
                                        : 'text.disabled',
                                    cursor: canRankByValue
                                        ? 'pointer'
                                        : 'default',
                                    '&:hover': canRankByValue
                                        ? { borderColor: 'text.secondary' }
                                        : {},
                                }}
                            >
                                <ArrowDownwardIcon
                                    sx={{
                                        fontSize: 13,
                                        transition: 'transform 0.15s',
                                        transform: sortAsc
                                            ? 'rotate(180deg)'
                                            : 'none',
                                    }}
                                />
                            </Box>
                        </Box>
                    </Tooltip>
                </Box>
                {!canRankByValue && (
                    <Typography sx={CONTROL_HINT_SX}>
                        Zoom the chart to rank by volume or price — the list
                        loads newest-first, so a ranking only holds inside a
                        window.
                    </Typography>
                )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.625 }}>
                <Typography sx={LABEL_SX}>Min volume</Typography>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    aria-label="Min volume"
                    value={minVolume}
                    onChange={(_, v) =>
                        v !== null && onMinVolumeChange(v as number)
                    }
                    sx={GROUP_SX}
                >
                    {MIN_VOLUME_OPTIONS.map(v => (
                        <ToggleButton
                            key={v}
                            value={v}
                            disabled={v !== 0 && !canRankByValue}
                            sx={{ flexGrow: 1 }}
                        >
                            {v === 0 ? 'Any' : formatVolume(v)}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                {!canRankByValue && (
                    <Typography sx={CONTROL_HINT_SX}>
                        A threshold over the whole event is the same partial
                        claim as a ranking — it needs a window too.
                    </Typography>
                )}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                }}
            >
                <Typography
                    sx={{
                        fontSize: '10px',
                        fontFamily: 'monospace',
                        color: 'text.secondary',
                    }}
                >
                    {shownCount.toLocaleString()} of{' '}
                    {windowCount.toLocaleString()}
                    {hasMore ? '+' : ''} shown
                </Typography>
                <Box
                    component="button"
                    type="button"
                    onClick={onReset}
                    disabled={resetDisabled}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.375,
                        height: 24,
                        px: 1,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: '4px',
                        bgcolor: 'transparent',
                        fontSize: '10px',
                        fontWeight: 600,
                        fontFamily: 'inherit',
                        color: resetDisabled ? 'text.disabled' : 'text.primary',
                        cursor: resetDisabled ? 'default' : 'pointer',
                        '&:hover': resetDisabled
                            ? {}
                            : { borderColor: 'text.secondary' },
                    }}
                >
                    <RestartAltIcon sx={{ fontSize: 13 }} />
                    Reset
                </Box>
            </Box>
        </Box>
    );
}
